import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LifeBuoy, BookOpen, Wand2, Share2, CreditCard, ShieldCheck, LifeBuoy as Ticket } from "lucide-react";

const DOCS = [
  { Icon: Wand2, title: "Démarrer avec le Studio IA", desc: "Importez vos produits, choisissez un format et générez vos premiers visuels en 3 clics." },
  { Icon: Share2, title: "Connecter vos réseaux", desc: "Liez Meta, TikTok, Google, Pinterest et Snapchat depuis le Hub de diffusion." },
  { Icon: CreditCard, title: "Gérer vos crédits IA", desc: "Comprenez la consommation des crédits et comment recharger votre compte." },
  { Icon: ShieldCheck, title: "Sécurité & RGPD", desc: "Vos données et catalogues sont chiffrés et conformes à la réglementation européenne." },
];

const HELP_FAQ = [
  { q: "Combien de crédits consomme une génération ?", a: "Un visuel IA coûte 15 crédits, une accroche publicitaire 5 crédits par variante. Les crédits se rechargent chaque mois selon votre abonnement." },
  { q: "Comment restaurer une ancienne publicité ?", a: "Toutes vos créations sont conservées dans la Bibliothèque cloud. Vous pouvez les retrouver, les télécharger ou les réutiliser à tout moment." },
  { q: "Pourquoi ma campagne est-elle en attente ?", a: "Les campagnes programmées se lancent à la date choisie. Vous pouvez modifier le statut depuis le Hub de diffusion." },
  { q: "Comment contacter le support humain ?", a: "Envoyez un ticket via le formulaire ci-dessous. Nos conseillers répondent sous 24h ouvrées (support prioritaire pour les plans Scale et Agence)." },
];

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, type: "support" });
      toast.success("Ticket envoyé ! Un conseiller vous recontactera.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Erreur d'envoi"); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24 px-6 mg-radial">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary flex items-center justify-center gap-2"><LifeBuoy size={14} /> SAV & Support</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3">Centre d'aide Monaga Adflow</h1>
            <p className="text-muted-foreground mt-4 text-lg">Documentation, réponses rapides et support humain. Nous sommes là pour vous.</p>
          </div>

          <div className="mb-12">
            <h2 className="font-display text-2xl font-bold mb-5 flex items-center gap-2"><BookOpen size={22} className="text-primary" /> Centre de documentation</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {DOCS.map((d) => (
                <div key={d.title} className="p-5 rounded-2xl border border-border bg-card flex items-start gap-3 hover:border-primary/40 transition-all">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><d.Icon size={18} className="text-primary" /></div>
                  <div><p className="font-semibold">{d.title}</p><p className="text-sm text-muted-foreground mt-1">{d.desc}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-display text-2xl font-bold mb-5">Questions d'aide fréquentes</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {HELP_FAQ.map((f, i) => (
                  <AccordionItem key={i} value={`h-${i}`} className="border border-border rounded-2xl px-5 bg-card">
                    <AccordionTrigger className="text-left font-medium hover:no-underline text-sm">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div>
              <h2 className="font-display text-2xl font-bold mb-5 flex items-center gap-2"><Ticket size={22} className="text-primary" /> Ouvrir un ticket</h2>
              <form onSubmit={submit} className="p-6 rounded-2xl border border-border bg-card space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="support-name" /></div>
                  <div><Label>E-mail</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="support-email" /></div>
                </div>
                <div><Label>Sujet</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="support-subject" /></div>
                <div><Label>Décrivez votre problème</Label><Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl min-h-[110px]" data-testid="support-message" /></div>
                <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white border-0" data-testid="support-submit">{loading ? "Envoi…" : "Envoyer le ticket"}</Button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
